/**
 * AssistentusApp side-effect wiring for completion feedback (Phase R1 of doc 06).
 *
 * @unia/task-ui dispatches `task-completed` on the window from
 * fireTaskCompleted(). We listen here and trigger native haptics on iOS —
 * the shared package never imports Capacitor.
 *
 * Subscribe once at app root via `useCompletionHaptics()`.
 *
 * Also installs `window.fireCompletionFeedback({ haptic })` — a synchronous
 * helper the shared package (e.g. GiperGantt drag handlers) can call without
 * importing Capacitor. The helper kicks off a Capacitor Haptics impact when
 * running natively, and degrades to Web Vibration API on Android, no-op on
 * desktop / iOS Safari.
 */
import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';

type HapticKind = 'light' | 'medium' | 'success';

async function triggerNative(kind: HapticKind) {
  try {
    if (!Capacitor.isNativePlatform()) return;
    const { Haptics, ImpactStyle, NotificationType } = await import('@capacitor/haptics');
    if (kind === 'success') {
      await Haptics.notification({ type: NotificationType.Success });
    } else {
      await Haptics.impact({ style: kind === 'light' ? ImpactStyle.Light : ImpactStyle.Medium });
    }
  } catch {
    // Best-effort — silently degrade.
  }
}

function installShim() {
  const w = window as any;
  if (w.__uniaHapticShimInstalled) return;
  w.__uniaHapticShimInstalled = true;
  w.fireCompletionFeedback = (opts?: { haptic?: HapticKind }) => {
    const kind: HapticKind = opts?.haptic ?? 'success';
    void triggerNative(kind);
    // Web fallback — Android Chrome only; iOS Safari/Capacitor WebView ignore.
    try { (navigator as any).vibrate?.(kind === 'light' ? 8 : kind === 'medium' ? 15 : 25); } catch { /* noop */ }
  };
}

export function useCompletionHaptics(): void {
  useEffect(() => {
    installShim();
    const handler = () => {
      void triggerNative('success');
    };
    window.addEventListener('task-completed', handler);
    return () => window.removeEventListener('task-completed', handler);
  }, []);
}
